import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Calendar, Plus, UserPlus, IndianRupee, X, Edit, Search, Filter, Trash2, RotateCcw, Settings } from 'lucide-react';
import { IconTip } from '../components/IconTip';
import { 
  getBookings, createBooking, updateBooking, collectBookingDeposit, 
  convertBookingToGuest, cancelBooking, retrieveBooking, deleteBooking,
  getBookingPolicy, updateBookingPolicy,
  getProperties, getRooms 
} from '../lib/api';
import { getISTDateString, formatDate } from '../lib/dateUtils';
import { useAuth } from '../context/AuthContext';

export default function Bookings() {
  const { canAdd, canEdit, canDelete, isAdmin } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [collectDepositDialogOpen, setCollectDepositDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Booking auto-cancel policy (per organisation)
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [holdDays, setHoldDays] = useState(0); // 0 = Never
  const [policyNever, setPolicyNever] = useState(true);
  const [policyDaysInput, setPolicyDaysInput] = useState('10');
  const [savingPolicy, setSavingPolicy] = useState(false);
  
  const [selectedBooking, setSelectedBooking] = useState(null);
  
  // Form states
  const [newBooking, setNewBooking] = useState({
    name: '',
    phone: '',
    email: '',
    expected_joining_date: '',
    preferred_property_id: '',
    preferred_room_id: '',
    preferred_bed_no: '',
    monthly_rent: '',
    deposit_amount: '',
    deposit_paid: '',
    payment_mode: 'cash',
    deposit_date: getISTDateString(),
    collect_deposit_now: false,
    remarks: ''
  });
  
  const [depositForm, setDepositForm] = useState({
    amount: '',
    payment_mode: 'cash',
    payment_date: getISTDateString(),
    remarks: ''
  });
  
  const [convertForm, setConvertForm] = useState({
    property_id: '',
    room_id: '',
    bed_no: '',
    joining_date: '',
    monthly_rent: '',
    deposit_amount: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter === 'active') {
        // Don't filter, will filter client-side
      } else if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const [bookingsRes, propertiesRes] = await Promise.all([
        getBookings(params),
        getProperties()
      ]);
      
      let bookingsData = bookingsRes.data;
      if (statusFilter === 'active') {
        bookingsData = bookingsData.filter(b => ['pending', 'confirmed'].includes(b.status));
      }
      
      setBookings(bookingsData);
      setProperties(propertiesRes.data);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    loadPolicy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRooms = async (propertyId) => {
    if (!propertyId) {
      setRooms([]);
      return;
    }
    try {
      const res = await getRooms(propertyId);
      // Sort rooms by combined_number (alphanumeric sorting)
      const sortedRooms = [...res.data].sort((a, b) => {
        const aNum = a.combined_number || '';
        const bNum = b.combined_number || '';
        return aNum.localeCompare(bNum, undefined, { numeric: true, sensitivity: 'base' });
      });
      setRooms(sortedRooms);
    } catch (error) {
      toast.error('Failed to load rooms');
    }
  };

  const handleAddBooking = async () => {
    if (!newBooking.name || !newBooking.phone || !newBooking.expected_joining_date) {
      toast.error('Please fill required fields: Name, Phone, Expected Joining Date');
      return;
    }
    
    try {
      const data = {
        name: newBooking.name,
        phone: newBooking.phone,
        email: newBooking.email || null,
        expected_joining_date: newBooking.expected_joining_date,
        preferred_property_id: newBooking.preferred_property_id || null,
        preferred_room_id: newBooking.preferred_room_id || null,
        preferred_bed_no: newBooking.preferred_bed_no ? parseInt(newBooking.preferred_bed_no) : null,
        monthly_rent: parseFloat(newBooking.monthly_rent) || 0,
        deposit_amount: parseFloat(newBooking.deposit_amount) || 0,
        deposit_paid: newBooking.collect_deposit_now ? (parseFloat(newBooking.deposit_paid) || 0) : 0,
        payment_mode: newBooking.collect_deposit_now ? newBooking.payment_mode : null,
        deposit_date: newBooking.collect_deposit_now ? newBooking.deposit_date : null,
        remarks: newBooking.remarks || null
      };
      
      const res = await createBooking(data);
      toast.success(res.data.message);
      setAddDialogOpen(false);
      resetNewBookingForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    }
  };

  const handleUpdateBooking = async () => {
    if (!selectedBooking) return;
    
    try {
      const data = {
        name: newBooking.name,
        phone: newBooking.phone,
        email: newBooking.email || null,
        expected_joining_date: newBooking.expected_joining_date,
        preferred_property_id: newBooking.preferred_property_id || null,
        preferred_room_id: newBooking.preferred_room_id || null,
        preferred_bed_no: newBooking.preferred_bed_no ? parseInt(newBooking.preferred_bed_no) : null,
        monthly_rent: parseFloat(newBooking.monthly_rent) || 0,
        deposit_amount: parseFloat(newBooking.deposit_amount) || 0,
        remarks: newBooking.remarks || null
      };
      
      await updateBooking(selectedBooking.id, data);
      toast.success('Booking updated successfully');
      setEditDialogOpen(false);
      setSelectedBooking(null);
      resetNewBookingForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking');
    }
  };

  const handleCollectDeposit = async () => {
    if (!selectedBooking || !depositForm.amount) {
      toast.error('Please enter deposit amount');
      return;
    }
    
    try {
      const res = await collectBookingDeposit({
        booking_id: selectedBooking.id,
        amount: parseFloat(depositForm.amount),
        payment_mode: depositForm.payment_mode,
        payment_date: depositForm.payment_date,
        remarks: depositForm.remarks || null
      });
      toast.success(res.data.message);
      setCollectDepositDialogOpen(false);
      setSelectedBooking(null);
      setDepositForm({ amount: '', payment_mode: 'cash', payment_date: getISTDateString(), remarks: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to collect deposit');
    }
  };

  const handleConvertToGuest = async () => {
    if (!selectedBooking) return;
    if (!convertForm.property_id || !convertForm.room_id || !convertForm.bed_no || !convertForm.joining_date) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      // Automatically derive rent_due_day from joining_date
      const rentDueDay = new Date(convertForm.joining_date).getDate();
      
      const res = await convertBookingToGuest({
        booking_id: selectedBooking.id,
        property_id: convertForm.property_id,
        room_id: convertForm.room_id,
        bed_no: parseInt(convertForm.bed_no),
        joining_date: convertForm.joining_date,
        monthly_rent: parseFloat(convertForm.monthly_rent) || selectedBooking.monthly_rent,
        deposit_amount: parseFloat(convertForm.deposit_amount) || selectedBooking.deposit_amount,
        rent_due_day: rentDueDay
      });
      toast.success(`${res.data.message}. Deposit ₹${res.data.deposit_transferred} transferred.`);
      setConvertDialogOpen(false);
      setSelectedBooking(null);
      resetConvertForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert booking');
    }
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking) return;
    
    try {
      const res = await cancelBooking(selectedBooking.id);
      toast.success(`${res.data.message}. Forfeited deposit: ₹${res.data.forfeited_deposit}`);
      setCancelDialogOpen(false);
      setSelectedBooking(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const handleRetrieveBooking = async (booking) => {
    try {
      const res = await retrieveBooking(booking.id);
      toast.success(`Booking retrieved — active again (${res.data.status})`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retrieve booking');
    }
  };

  const loadPolicy = async () => {
    try {
      const res = await getBookingPolicy();
      const days = res.data.booking_hold_days || 0;
      setHoldDays(days);
      setPolicyNever(days <= 0);
      setPolicyDaysInput(days > 0 ? String(days) : '10');
    } catch (e) { /* non-blocking */ }
  };

  const openPolicyDialog = () => {
    setPolicyNever(holdDays <= 0);
    setPolicyDaysInput(holdDays > 0 ? String(holdDays) : '10');
    setPolicyDialogOpen(true);
  };

  const handleSavePolicy = async () => {
    let value = 0;
    if (!policyNever) {
      const n = parseInt(policyDaysInput, 10);
      if (!n || n < 1 || n > 30) {
        toast.error('Enter a number of days between 1 and 30, or turn on "Never".');
        return;
      }
      value = n;
    }
    try {
      setSavingPolicy(true);
      const res = await updateBookingPolicy(value);
      setHoldDays(res.data.booking_hold_days || 0);
      toast.success(value === 0 ? 'Auto-cancel turned off (Never)' : `Bookings will auto-cancel after ${value} days`);
      setPolicyDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update policy');
    } finally {
      setSavingPolicy(false);
    }
  };

  const handleDeleteBooking = async () => {
    if (!selectedBooking) return;
    
    try {
      await deleteBooking(selectedBooking.id);
      toast.success('Booking deleted. Receipts preserved.');
      setDeleteDialogOpen(false);
      setSelectedBooking(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete booking');
    }
  };

  const resetNewBookingForm = () => {
    setNewBooking({
      name: '',
      phone: '',
      email: '',
      expected_joining_date: '',
      preferred_property_id: '',
      preferred_room_id: '',
      preferred_bed_no: '',
      monthly_rent: '',
      deposit_amount: '',
      deposit_paid: '',
      payment_mode: 'cash',
      deposit_date: getISTDateString(),
      collect_deposit_now: false,
      remarks: ''
    });
  };

  const resetConvertForm = () => {
    setConvertForm({
      property_id: '',
      room_id: '',
      bed_no: '',
      joining_date: '',
      monthly_rent: '',
      deposit_amount: ''
    });
  };

  const openEditDialog = (booking) => {
    setSelectedBooking(booking);
    setNewBooking({
      name: booking.name,
      phone: booking.phone,
      email: booking.email || '',
      expected_joining_date: booking.expected_joining_date,
      preferred_property_id: booking.preferred_property_id || '',
      preferred_room_id: booking.preferred_room_id || '',
      preferred_bed_no: booking.preferred_bed_no ? booking.preferred_bed_no.toString() : '',
      monthly_rent: booking.monthly_rent?.toString() || '',
      deposit_amount: booking.deposit_amount?.toString() || '',
      deposit_paid: '',
      payment_mode: 'cash',
      deposit_date: getISTDateString(),
      collect_deposit_now: false,
      remarks: booking.remarks || ''
    });
    if (booking.preferred_property_id) {
      fetchRooms(booking.preferred_property_id);
    }
    setEditDialogOpen(true);
  };

  const openConvertDialog = (booking) => {
    setSelectedBooking(booking);
    // Get the day of month from joining date to use as default rent due day
    const joiningDate = getISTDateString();
    
    setConvertForm({
      property_id: booking.preferred_property_id || '',
      room_id: booking.preferred_room_id || '',
      bed_no: '',
      joining_date: joiningDate,
      monthly_rent: booking.monthly_rent?.toString() || '',
      deposit_amount: booking.deposit_amount?.toString() || ''
    });
    if (booking.preferred_property_id) {
      fetchRooms(booking.preferred_property_id);
    }
    setConvertDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      confirmed: 'bg-blue-100 text-blue-700',
      converted: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-rose-100 text-rose-700'
    };
    const dot = {
      pending: 'bg-amber-500',
      confirmed: 'bg-blue-500',
      converted: 'bg-emerald-500',
      cancelled: 'bg-rose-500'
    };
    const labels = {
      pending: 'Pending',
      confirmed: 'Confirmed',
      converted: 'Converted',
      cancelled: 'Cancelled'
    };
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-100 text-slate-700'}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${dot[status] || 'bg-slate-500'}`}></span>
        {labels[status] || status}
      </span>
    );
  };

  const filteredBookings = bookings.filter(b => 
    (b.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.phone?.includes(searchTerm)) &&
    (selectedProperty === 'all' || b.preferred_property_id === selectedProperty)
  );

  const vacantBeds = rooms.find(r => r.id === convertForm.room_id)?.beds?.filter(b => b.status === 'vacant') || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Advance Bookings</h1>
          <p className="text-slate-500">
            Manage advance bookings and convert to guests
            {holdDays > 0
              ? <span className="ml-1 text-amber-600">· Auto-cancel after {holdDays} days</span>
              : <span className="ml-1 text-slate-400">· Auto-cancel: Off</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin() && (
            <Button variant="outline" onClick={openPolicyDialog} data-testid="booking-policy-btn">
              <Settings className="w-4 h-4 mr-2" /> Booking Policy
            </Button>
          )}
          {canAdd('bookings') && (
            <Button onClick={() => setAddDialogOpen(true)} className="bg-blue-800 hover:bg-blue-900" data-testid="add-booking-btn">
              <Plus className="w-4 h-4 mr-2" /> New Booking
            </Button>
          )}
          {properties.length > 1 && (
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="w-[190px]" data-testid="bookings-property-select">
                <SelectValue placeholder="All Properties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4 items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-bookings"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card className="dash-animate relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-indigo-600" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Bookings ({filteredBookings.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading...</div>
          ) : filteredBookings.length === 0 ? (
            <div className="text-center py-14">
              <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-1">No bookings yet</h3>
              <p className="text-slate-500 mb-5 text-sm">Create an advance booking to reserve a bed before the guest arrives.</p>
              {canAdd('bookings') && (
                <Button onClick={() => setAddDialogOpen(true)} className="bg-blue-800 hover:bg-blue-900" data-testid="empty-new-booking-btn">
                  <Plus className="w-4 h-4 mr-2" /> New Booking
                </Button>
              )}
            </div>
          ) : (
            <Table className="whitespace-nowrap">
              <TableHeader>
                <TableRow>
                  <TableHead>Guest ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Booking Date</TableHead>
                  <TableHead>Expected Joining</TableHead>
                  <TableHead>Preferred Room</TableHead>
                  <TableHead className="text-right">Rent</TableHead>
                  <TableHead className="text-right">Deposit</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBookings.map(booking => (
                  <TableRow key={booking.id} data-testid={`booking-row-${booking.id}`} className="transition-colors hover:bg-slate-50">
                    <TableCell><span className="px-2 py-0.5 rounded text-xs font-mono font-semibold bg-indigo-50 text-indigo-700 whitespace-nowrap inline-block">{booking.guest_code || '—'}</span></TableCell>
                    <TableCell className="font-medium">{booking.name}</TableCell>
                    <TableCell className="font-mono text-sm text-slate-600">{booking.phone}</TableCell>
                    <TableCell className="font-mono text-sm text-slate-600">{booking.booking_date ? formatDate(booking.booking_date) : '-'}</TableCell>
                    <TableCell className="font-mono text-sm">{booking.expected_joining_date ? formatDate(booking.expected_joining_date) : '-'}</TableCell>
                    <TableCell>
                      {booking.preferred_room_name || booking.preferred_property_name || '-'}
                    </TableCell>
                    <TableCell className="text-right">₹{(booking.monthly_rent || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <span className="font-medium">₹{(booking.deposit_paid || 0).toLocaleString()}</span>
                      <span className="text-slate-400 text-xs"> / ₹{(booking.deposit_amount || 0).toLocaleString()}</span>
                    </TableCell>
                    <TableCell>{getStatusBadge(booking.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {['pending', 'confirmed'].includes(booking.status) && canEdit('bookings') && (
                          <>
                            <IconTip label="Convert to Guest"><Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => openConvertDialog(booking)}
                              className="h-8 w-8 text-emerald-600" 
                              data-testid={`convert-btn-${booking.id}`}
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button></IconTip>
                            <IconTip label="Collect Deposit"><Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => {
                                setSelectedBooking(booking);
                                setDepositForm({ amount: '', payment_mode: 'cash', payment_date: getISTDateString(), remarks: '' });
                                setCollectDepositDialogOpen(true);
                              }}
                              className="h-8 w-8 text-blue-600" 
                              data-testid={`collect-deposit-btn-${booking.id}`}
                            >
                              <IndianRupee className="h-4 w-4" />
                            </Button></IconTip>
                            <IconTip label="Edit"><Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => openEditDialog(booking)}
                              className="h-8 w-8" 
                              data-testid={`edit-btn-${booking.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button></IconTip>
                            <IconTip label="Cancel Booking"><Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => {
                                setSelectedBooking(booking);
                                setCancelDialogOpen(true);
                              }}
                              className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50" 
                              data-testid={`cancel-btn-${booking.id}`}
                            >
                              <X className="h-4 w-4" />
                            </Button></IconTip>
                          </>
                        )}
                        {booking.status === 'cancelled' && canEdit('bookings') && (
                          <IconTip label="Retrieve Booking (re-activate for 10 days)"><Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleRetrieveBooking(booking)}
                            className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" 
                            data-testid={`retrieve-btn-${booking.id}`}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button></IconTip>
                        )}
                        {/* Delete button for all bookings */}
                        {canDelete('bookings') && (
                          <IconTip label="Delete Booking"><Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => {
                              setSelectedBooking(booking);
                              setDeleteDialogOpen(true);
                            }}
                            className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50" 
                            data-testid={`delete-btn-${booking.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button></IconTip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Booking Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Advance Booking</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={newBooking.name}
                  onChange={(e) => setNewBooking({...newBooking, name: e.target.value.toUpperCase()})}
                  placeholder="GUEST NAME"
                  data-testid="booking-name"
                  style={{ textTransform: 'uppercase' }}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  value={newBooking.phone}
                  onChange={(e) => setNewBooking({...newBooking, phone: e.target.value})}
                  placeholder="Phone number"
                  data-testid="booking-phone"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newBooking.email}
                  onChange={(e) => setNewBooking({...newBooking, email: e.target.value.toLowerCase()})}
                  placeholder="email (optional)"
                  style={{ textTransform: 'lowercase' }}
                />
              </div>
              <div className="space-y-2">
                <Label>Expected Joining Date *</Label>
                <Input
                  type="date"
                  value={newBooking.expected_joining_date}
                  onChange={(e) => setNewBooking({...newBooking, expected_joining_date: e.target.value})}
                  data-testid="booking-joining-date"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Preferred Property</Label>
              <Select 
                value={newBooking.preferred_property_id} 
                onValueChange={(val) => {
                  setNewBooking({...newBooking, preferred_property_id: val, preferred_room_id: '', preferred_bed_no: ''});
                  fetchRooms(val);
                }}
              >
                <SelectTrigger data-testid="booking-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>
                  {properties.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Preferred Room</Label>
                <Select 
                  value={newBooking.preferred_room_id} 
                  onValueChange={(val) => setNewBooking({...newBooking, preferred_room_id: val, preferred_bed_no: ''})}
                  disabled={!newBooking.preferred_property_id}
                >
                  <SelectTrigger data-testid="booking-room-select"><SelectValue placeholder="Select room" /></SelectTrigger>
                  <SelectContent>
                    {rooms.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.combined_number}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Preferred Bed</Label>
                <Select 
                  value={newBooking.preferred_bed_no ? newBooking.preferred_bed_no.toString() : ''} 
                  onValueChange={(val) => setNewBooking({...newBooking, preferred_bed_no: val})}
                  disabled={!newBooking.preferred_room_id}
                >
                  <SelectTrigger data-testid="booking-bed-select"><SelectValue placeholder="Select bed" /></SelectTrigger>
                  <SelectContent>
                    {(rooms.find(r => r.id === newBooking.preferred_room_id)?.beds || []).map(b => (
                      <SelectItem key={b.bed_no} value={b.bed_no.toString()}>
                        Bed {b.bed_no} ({b.status === 'vacant' ? 'Vacant' : b.status === 'notice' ? 'On Notice' : 'Occupied'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monthly Rent (₹)</Label>
                <Input
                  type="number"
                  value={newBooking.monthly_rent}
                  onChange={(e) => setNewBooking({...newBooking, monthly_rent: e.target.value})}
                  placeholder="0"
                  data-testid="booking-rent"
                />
              </div>
              <div className="space-y-2">
                <Label>Deposit Amount (₹)</Label>
                <Input
                  type="number"
                  value={newBooking.deposit_amount}
                  onChange={(e) => setNewBooking({...newBooking, deposit_amount: e.target.value})}
                  placeholder="0"
                  data-testid="booking-deposit-amount"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <Checkbox
                id="collect_now"
                checked={newBooking.collect_deposit_now}
                onCheckedChange={(checked) => setNewBooking({...newBooking, collect_deposit_now: checked})}
              />
              <Label htmlFor="collect_now" className="cursor-pointer">Collect deposit now</Label>
            </div>
            
            {newBooking.collect_deposit_now && (
              <div className="grid grid-cols-3 gap-4 p-3 border rounded-lg bg-blue-50">
                <div className="space-y-2">
                  <Label>Amount (₹)</Label>
                  <Input
                    type="number"
                    value={newBooking.deposit_paid}
                    onChange={(e) => setNewBooking({...newBooking, deposit_paid: e.target.value})}
                    placeholder="Amount"
                    data-testid="booking-deposit-paid"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mode</Label>
                  <Select value={newBooking.payment_mode} onValueChange={(val) => setNewBooking({...newBooking, payment_mode: val})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank">Bank</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={newBooking.deposit_date}
                    onChange={(e) => setNewBooking({...newBooking, deposit_date: e.target.value})}
                    data-testid="booking-deposit-date"
                  />
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea
                value={newBooking.remarks}
                onChange={(e) => setNewBooking({...newBooking, remarks: e.target.value.toUpperCase()})}
                placeholder="ANY NOTES..."
                rows={2}
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => {setAddDialogOpen(false); resetNewBookingForm();}} className="flex-1">Cancel</Button>
              <Button onClick={handleAddBooking} className="flex-1 bg-blue-800 hover:bg-blue-900" data-testid="save-booking-btn">
                Create Booking
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Booking Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Booking - {selectedBooking?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={newBooking.name}
                  onChange={(e) => setNewBooking({...newBooking, name: e.target.value.toUpperCase()})}
                  style={{ textTransform: 'uppercase' }}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  value={newBooking.phone}
                  onChange={(e) => setNewBooking({...newBooking, phone: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newBooking.email}
                  onChange={(e) => setNewBooking({...newBooking, email: e.target.value.toLowerCase()})}
                  style={{ textTransform: 'lowercase' }}
                />
              </div>
              <div className="space-y-2">
                <Label>Expected Joining Date *</Label>
                <Input
                  type="date"
                  value={newBooking.expected_joining_date}
                  onChange={(e) => setNewBooking({...newBooking, expected_joining_date: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monthly Rent (₹)</Label>
                <Input
                  type="number"
                  value={newBooking.monthly_rent}
                  onChange={(e) => setNewBooking({...newBooking, monthly_rent: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Deposit Amount (₹)</Label>
                <Input
                  type="number"
                  value={newBooking.deposit_amount}
                  onChange={(e) => setNewBooking({...newBooking, deposit_amount: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea
                value={newBooking.remarks}
                onChange={(e) => setNewBooking({...newBooking, remarks: e.target.value.toUpperCase()})}
                rows={2}
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => {setEditDialogOpen(false); setSelectedBooking(null);}} className="flex-1">Cancel</Button>
              <Button onClick={handleUpdateBooking} className="flex-1 bg-blue-800 hover:bg-blue-900">
                Update Booking
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Collect Deposit Dialog */}
      <Dialog open={collectDepositDialogOpen} onOpenChange={setCollectDepositDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Collect Deposit - {selectedBooking?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                Current Deposit: <span className="font-medium">₹{(selectedBooking?.deposit_paid || 0).toLocaleString()}</span>
                {' / '}
                <span>₹{(selectedBooking?.deposit_amount || 0).toLocaleString()}</span>
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  value={depositForm.amount}
                  onChange={(e) => setDepositForm({...depositForm, amount: e.target.value})}
                  placeholder="Enter amount"
                  data-testid="collect-deposit-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Deposit Date</Label>
                <Input
                  type="date"
                  value={depositForm.payment_date}
                  onChange={(e) => setDepositForm({...depositForm, payment_date: e.target.value})}
                  data-testid="collect-deposit-date"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Payment Mode</Label>
              <Select value={depositForm.payment_mode} onValueChange={(val) => setDepositForm({...depositForm, payment_mode: val})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank">Bank</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Input
                value={depositForm.remarks}
                onChange={(e) => setDepositForm({...depositForm, remarks: e.target.value.toUpperCase()})}
                placeholder="OPTIONAL"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setCollectDepositDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleCollectDeposit} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="confirm-collect-deposit">
                Collect ₹{depositForm.amount || 0}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Convert to Guest Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Convert to Guest - {selectedBooking?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-800">
                Deposit collected: <span className="font-bold">₹{(selectedBooking?.deposit_paid || 0).toLocaleString()}</span>
                {' '}will be transferred to guest account.
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Property *</Label>
                <Select 
                  value={convertForm.property_id} 
                  onValueChange={(val) => {
                    setConvertForm({...convertForm, property_id: val, room_id: '', bed_no: ''});
                    fetchRooms(val);
                  }}
                >
                  <SelectTrigger data-testid="convert-property"><SelectValue placeholder="Select property" /></SelectTrigger>
                  <SelectContent>
                    {properties.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Room *</Label>
                <Select 
                  value={convertForm.room_id} 
                  onValueChange={(val) => setConvertForm({...convertForm, room_id: val, bed_no: ''})}
                >
                  <SelectTrigger data-testid="convert-room"><SelectValue placeholder="Select room" /></SelectTrigger>
                  <SelectContent>
                    {rooms.filter(r => r.beds?.some(b => b.status === 'vacant')).map(r => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.combined_number} ({r.beds?.filter(b => b.status === 'vacant').length} vacant)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bed *</Label>
                <Select value={convertForm.bed_no} onValueChange={(val) => setConvertForm({...convertForm, bed_no: val})}>
                  <SelectTrigger data-testid="convert-bed"><SelectValue placeholder="Select bed" /></SelectTrigger>
                  <SelectContent>
                    {vacantBeds.map(b => (
                      <SelectItem key={b.bed_no} value={b.bed_no.toString()}>Bed {b.bed_no}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Joining Date *</Label>
                <Input
                  type="date"
                  value={convertForm.joining_date}
                  onChange={(e) => {
                    setConvertForm({...convertForm, joining_date: e.target.value});
                  }}
                  data-testid="convert-joining-date"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monthly Rent (₹)</Label>
                <Input
                  type="number"
                  value={convertForm.monthly_rent}
                  onChange={(e) => setConvertForm({...convertForm, monthly_rent: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Total Deposit (₹)</Label>
                <Input
                  type="number"
                  value={convertForm.deposit_amount}
                  onChange={(e) => setConvertForm({...convertForm, deposit_amount: e.target.value})}
                />
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => {setConvertDialogOpen(false); setSelectedBooking(null);}} className="flex-1">Cancel</Button>
              <Button onClick={handleConvertToGuest} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="confirm-convert">
                Convert to Guest
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Booking</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-red-800 font-medium">Are you sure you want to cancel this booking?</p>
              <p className="text-red-600 text-sm mt-2">
                Deposit of <span className="font-bold">₹{(selectedBooking?.deposit_paid || 0).toLocaleString()}</span> will be forfeited (non-refundable).
              </p>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setCancelDialogOpen(false)} className="flex-1">Keep Booking</Button>
              <Button onClick={handleCancelBooking} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-cancel">
                Cancel Booking
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Booking Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Booking</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-slate-800 font-medium">Delete booking for "{selectedBooking?.name}"?</p>
              <p className="text-slate-600 text-sm mt-2">
                This will permanently remove the booking record.
              </p>
              {selectedBooking?.deposit_paid > 0 && (
                <p className="text-blue-600 text-sm mt-2 font-medium">
                  Note: Associated receipts (₹{(selectedBooking?.deposit_paid || 0).toLocaleString()}) will be preserved in Daily Hisab.
                </p>
              )}
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleDeleteBooking} className="flex-1 bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete">
                Delete Booking
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Auto-Cancel Policy Dialog (admin only) */}
      <Dialog open={policyDialogOpen} onOpenChange={setPolicyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Booking Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Set how many days a booking stays active before it is automatically cancelled (deposit is kept and can be retrieved). Turn on "Never" if your company has no auto-cancel policy.
            </p>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={policyNever} onCheckedChange={(v) => setPolicyNever(!!v)} data-testid="policy-never-toggle" />
              <span className="text-sm font-medium text-slate-800">Never auto-cancel</span>
            </label>
            <div className={policyNever ? 'opacity-40 pointer-events-none' : ''}>
              <Label>Auto-cancel after (days)</Label>
              <Input
                type="number"
                min="1"
                max="30"
                value={policyDaysInput}
                onChange={(e) => setPolicyDaysInput(e.target.value)}
                disabled={policyNever}
                className="mt-1 w-40"
                data-testid="policy-days-input"
              />
              <p className="text-xs text-slate-400 mt-1">Any value from 1 to 30 days.</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setPolicyDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSavePolicy} disabled={savingPolicy} className="flex-1 bg-blue-800 hover:bg-blue-900" data-testid="save-policy-btn">
                {savingPolicy ? 'Saving…' : 'Save Policy'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
